import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import StatusBadge from '../../../components/shared/StatusBadge';
import { formatDate, formatCurrency, formatDatetime } from '../../../utils/formatters';
import { ArrowLeft, Trash2 } from 'lucide-react';

const NEXT_STATUS = {
  'Recebido': 'Em Produção',
  'Em Produção': 'Pronto',
  'Pronto': 'Em Entrega',
  'Em Entrega': 'Entregue',
};

export default function PedidoDetalheDash() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/pedidos/${id}`)
      .then(r => setPedido(r.data))
      .catch(() => navigate('/admin/pedidos'))
      .finally(() => setLoading(false));
  }, [id]);

  const changeStatus = async (newStatus, mot = '') => {
    setUpdating(true);
    setError('');
    try {
      const res = await api.put(`/pedidos/${id}/status`, { status: newStatus, motivo: mot });
      setPedido(p => ({ ...p, status: newStatus }));
      setShowCancel(false);
    } catch (err) {
      setError(err.userMessage || 'Erro ao alterar status');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Excluir este pedido permanentemente?')) return;
    await api.delete(`/pedidos/${id}`);
    navigate('/admin/pedidos');
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!pedido) return null;

  const nextStatus = NEXT_STATUS[pedido.status];

  return (
    <div data-testid="pedido-detalhe-dash">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-brand-bg"><ArrowLeft className="w-5 h-5 text-brand-text-muted" /></button>
        <div className="flex-1">
          <h1 className="dashboard-heading text-xl">Pedido #{pedido.numero}</h1>
          <p className="text-brand-text-muted text-sm font-body">{pedido.cliente_nome} · {formatDate(pedido.data_pedido)}</p>
        </div>
        <StatusBadge status={pedido.status} size="md" />
        {isAdmin && pedido.status !== 'Cancelado' && (
          <button onClick={handleDelete} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid="delete-pedido-btn"><Trash2 className="w-4 h-4" /></button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Info */}
        <div className="md:col-span-2 space-y-4">
          <div className="card-dashboard">
            <h3 className="font-dashboard font-semibold text-brand-text mb-3">Itens do Pedido</h3>
            <table className="w-full text-sm font-body">
              <thead><tr className="border-b border-brand-border"><th className="text-left py-2 text-xs text-brand-text-muted font-dashboard">Produto</th><th className="text-center py-2 text-xs text-brand-text-muted font-dashboard">Qtd</th><th className="text-center py-2 text-xs text-brand-text-muted font-dashboard">Un</th>{isAdmin && <><th className="text-right py-2 text-xs text-brand-text-muted font-dashboard">Unit.</th><th className="text-right py-2 text-xs text-brand-text-muted font-dashboard">Total</th></>}</tr></thead>
              <tbody>
                {pedido.itens?.map((item, i) => (
                  <tr key={i} className="border-b border-brand-border last:border-0">
                    <td className="py-2.5"><p className="text-brand-text">{item.descricao}</p><p className="text-xs text-brand-text-muted">{item.sku}</p></td>
                    <td className="py-2.5 text-center font-bold">{item.quantidade}</td>
                    <td className="py-2.5 text-center text-brand-text-muted">{item.unidade}</td>
                    {isAdmin && <><td className="py-2.5 text-right">{item.preco_unitario != null ? formatCurrency(item.preco_unitario) : '–'}</td><td className="py-2.5 text-right font-semibold">{item.preco_total != null ? formatCurrency(item.preco_total) : '–'}</td></>}
                  </tr>
                ))}
              </tbody>
              {isAdmin && pedido.total != null && (
                <tfoot><tr><td colSpan={4} className="py-2.5 text-right font-dashboard font-bold">Total</td><td className="py-2.5 text-right font-dashboard font-bold text-brand-primary">{formatCurrency(pedido.total)}</td></tr></tfoot>
              )}
            </table>
          </div>

          {/* History */}
          {pedido.historico?.length > 0 && (
            <div className="card-dashboard">
              <h3 className="font-dashboard font-semibold text-brand-text mb-3">Histórico de Status</h3>
              <div className="space-y-2">
                {pedido.historico.map((h, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm font-body">
                    <div className="w-2 h-2 bg-brand-primary rounded-full mt-1.5 flex-shrink-0" />
                    <div>
                      <p className="text-brand-text">{h.status_anterior ? `${h.status_anterior} → ` : ''}<strong>{h.status_novo}</strong></p>
                      <p className="text-xs text-brand-text-muted">{formatDatetime(h.data_hora)}{h.motivo && ` · ${h.motivo}`}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <div className="card-dashboard">
            <h3 className="font-dashboard font-semibold text-brand-text mb-3">Informações</h3>
            <div className="space-y-2 text-sm font-body">
              <div className="flex justify-between"><span className="text-brand-text-muted">Prazo</span><span className="font-medium">{pedido.prazo}</span></div>
              <div className="flex justify-between"><span className="text-brand-text-muted">Turno</span><span className="font-medium">{pedido.turno || '–'}</span></div>
              <div className="flex justify-between"><span className="text-brand-text-muted">Tipo</span><span className="font-medium">{pedido.tipo_entrega || '–'}</span></div>
              <div className="flex justify-between"><span className="text-brand-text-muted">Rota</span><span className="font-medium">{pedido.rota_nome || '–'}</span></div>
            </div>
          </div>

          <div className="card-dashboard space-y-2">
            <h3 className="font-dashboard font-semibold text-brand-text mb-3">Ações</h3>
            {nextStatus && (
              <button onClick={() => changeStatus(nextStatus)} disabled={updating} className="w-full py-2.5 rounded-lg btn-primary font-dashboard font-semibold text-sm disabled:opacity-60" data-testid="advance-status-btn">
                {updating ? '...' : `Avançar para: ${nextStatus}`}
              </button>
            )}
            {pedido.status !== 'Cancelado' && pedido.status !== 'Entregue' && (
              <>
                {showCancel ? (
                  <div className="space-y-2">
                    <textarea className="w-full rounded-lg border border-brand-border p-2.5 text-sm font-body h-20 resize-none" placeholder="Motivo (obrigatório)" value={motivo} onChange={e => setMotivo(e.target.value)} data-testid="cancel-motivo" />
                    <button onClick={() => { if (!motivo) return setError('Informe o motivo'); changeStatus('Cancelado', motivo); }} disabled={updating} className="w-full py-2 rounded-lg bg-red-600 text-white text-sm font-dashboard font-semibold" data-testid="confirm-cancel-btn">Confirmar Cancelamento</button>
                    <button onClick={() => setShowCancel(false)} className="w-full py-2 rounded-lg border border-brand-border text-sm font-body">Cancelar</button>
                  </div>
                ) : (
                  <button onClick={() => setShowCancel(true)} className="w-full py-2.5 rounded-lg border-2 border-red-200 text-red-600 font-dashboard font-medium text-sm" data-testid="cancel-order-btn">Cancelar Pedido</button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
